import React, { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, addDays, isSameDay, startOfDay } from 'date-fns';
import { Calendar as CalendarIcon, Clock, ChevronRight, CheckCircle2 } from 'lucide-react';
import { schedulesApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import 'react-day-picker/dist/style.css';

const ScheduleBooking = () => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [step, setStep] = useState(1); // 1: Date, 2: Time, 3: Details, 4: Success
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { toast } = useToast();

    const timeSlots = [
        '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
        '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM'
    ];

    const handleBooking = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            // Combine date and time
            const [time, period] = selectedSlot.split(' ');
            let [hours, minutes] = time.split(':');
            hours = parseInt(hours);
            if (period === 'PM' && hours !== 12) hours += 12;
            if (period === 'AM' && hours === 12) hours = 0;

            const scheduledDate = new Date(selectedDate);
            scheduledDate.setHours(hours, parseInt(minutes), 0);

            await schedulesApi.createSchedule({
                ...formData,
                scheduledDate: scheduledDate.toISOString(),
                duration: 30
            });

            setStep(4);
            toast({
                title: "Meeting Scheduled!",
                description: "Your session has been booked successfully.",
            });
        } catch (error) {
            toast({
                title: "Booking Failed",
                description: "Please try again later.",
                variant: "destructive"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <h3 className="text-xl font-bold text-silver mb-6 flex items-center gap-2">
                            <CalendarIcon className="text-silver" />
                            Select a Date
                        </h3>
                        <div className="glass-morphism p-4 rounded-2xl border border-gray-800 flex justify-center booking-calendar">
                            <style>{`
                .booking-calendar .rdp {
                  --rdp-cell-size: 40px;
                  --rdp-accent-color: #c0c0c0;
                  --rdp-background-color: rgba(255, 255, 255, 0.1);
                  margin: 0;
                }
                .booking-calendar .rdp-day_selected {
                   background-color: var(--rdp-accent-color) !important;
                   color: black !important;
                   font-weight: bold;
                }
                .booking-calendar .rdp-day:hover:not(.rdp-day_selected) {
                  background-color: rgba(255,255,255,0.1);
                }
                .rdp-head_cell {
                  color: #888;
                  font-weight: 500;
                }
                .rdp-tfoot {
                  display: none;
                }
              `}</style>
                            <DayPicker
                                mode="single"
                                selected={selectedDate}
                                onSelect={(date) => date && setSelectedDate(date)}
                                disabled={{ before: new Date() }}
                            />
                        </div>
                        <button
                            onClick={() => setStep(2)}
                            className="w-full mt-6 bg-silver text-black py-3 rounded-xl font-bold hover:bg-white transition-all flex items-center justify-center gap-2"
                        >
                            Select Time Slot <ChevronRight size={20} />
                        </button>
                    </div>
                );
            case 2:
                return (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <button onClick={() => setStep(1)} className="text-gray-400 hover:text-white mb-4 flex items-center gap-1 text-sm">
                            ← Change Date
                        </button>
                        <h3 className="text-xl font-bold text-silver mb-6 flex items-center gap-2">
                            <Clock className="text-silver" />
                            {format(selectedDate, 'MMMM do, yyyy')}
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            {timeSlots.map((slot) => (
                                <button
                                    key={slot}
                                    onClick={() => setSelectedSlot(slot)}
                                    className={`py-3 px-4 rounded-xl border transition-all duration-300 ${selectedSlot === slot
                                            ? 'bg-silver text-black border-silver font-bold shadow-lg'
                                            : 'border-gray-800 text-gray-400 hover:border-gray-600 hover:bg-white/5'
                                        }`}
                                >
                                    {slot}
                                </button>
                            ))}
                        </div>
                        <button
                            disabled={!selectedSlot}
                            onClick={() => setStep(3)}
                            className="w-full mt-8 bg-silver text-black py-3 rounded-xl font-bold hover:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            Next Step <ChevronRight size={20} />
                        </button>
                    </div>
                );
            case 3:
                return (
                    <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                        <button onClick={() => setStep(2)} className="text-gray-400 hover:text-white mb-4 flex items-center gap-1 text-sm">
                            ← Change Time
                        </button>
                        <h3 className="text-xl font-bold text-silver mb-2">Finalize Booking</h3>
                        <p className="text-gray-400 mb-6 text-sm">
                            Session with HARA-XY Team on {format(selectedDate, 'MMM do')} at {selectedSlot}
                        </p>
                        <form onSubmit={handleBooking} className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">Full Name</label>
                                <input
                                    required
                                    type="text"
                                    placeholder="John Doe"
                                    className="w-full bg-black/40 border border-gray-800 rounded-xl px-4 py-3 text-silver focus:outline-none focus:border-silver transition-colors"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">Work Email</label>
                                <input
                                    required
                                    type="email"
                                    placeholder="john@company.com"
                                    className="w-full bg-black/40 border border-gray-800 rounded-xl px-4 py-3 text-silver focus:outline-none focus:border-silver transition-colors"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">Company</label>
                                <input
                                    type="text"
                                    placeholder="Your Company"
                                    className="w-full bg-black/40 border border-gray-800 rounded-xl px-4 py-3 text-silver focus:outline-none focus:border-silver transition-colors"
                                    value={formData.company}
                                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">Notes (Optional)</label>
                                <textarea
                                    placeholder="What would you like to discuss?"
                                    className="w-full bg-black/40 border border-gray-800 rounded-xl px-4 py-3 text-silver focus:outline-none focus:border-silver transition-colors h-24 resize-none"
                                    value={formData.message}
                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                ></textarea>
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-white text-black py-3 rounded-xl font-bold hover:bg-silver transition-all flex items-center justify-center gap-2 mt-2"
                            >
                                {isSubmitting ? 'Confirming...' : 'Confirm Meeting'}
                            </button>
                        </form>
                    </div>
                );
            case 4:
                return (
                    <div className="text-center py-10 animate-in zoom-in duration-500">
                        <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 size={48} className="text-green-500" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Booking Confirmed!</h3>
                        <p className="text-gray-400 mb-8 max-w-xs mx-auto">
                            We've sent a calendar invitation to {formData.email}. See you on {format(selectedDate, 'MMM do')}!
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="text-silver hover:text-white font-medium border-b border-silver/30 hover:border-white transition-all pb-1"
                        >
                            Schedule another session
                        </button>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="max-w-md mx-auto">
            {renderStep()}
        </div>
    );
};

export default ScheduleBooking;
